/**
 * All secrets are handled with Web Crypto (available in the Convex runtime).
 * - deviceToken / accountKey: 32 random bytes, base64url, stored as sha256 hex.
 * - PIN: sha256 hex of (salt + pin) with a 16-byte random hex salt per member.
 */

const encoder = new TextEncoder();

function toHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

export async function sha256Hex(input: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(input));
  return toHex(new Uint8Array(digest));
}

export function randomToken(bytes = 32): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return toBase64Url(buf);
}

/**
 * An accountKey that starts with a letter or digit, so `circles login <key>` never
 * reads it as an option. Keys minted before this may start with "-" or "_" and
 * still validate (lookup is by hash).
 */
export function randomAccountKey(): string {
  for (;;) {
    const key = randomToken(32);
    if (/^[A-Za-z0-9]/.test(key)) return key;
  }
}

export function randomHex(bytes = 16): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return toHex(buf);
}

/** Invite codes are 12 random bytes → 16 base64url chars. */
export function randomInviteCode(): string {
  return randomToken(12);
}

export const PIN_RE = /^\d{4}$/;

export function isValidPin(pin: string): boolean {
  return PIN_RE.test(pin);
}

export async function hashPin(pin: string, salt: string): Promise<string> {
  return sha256Hex(salt + pin);
}

export async function newPinHash(pin: string): Promise<{ pinHash: string; pinSalt: string }> {
  const pinSalt = randomHex(16);
  const pinHash = await hashPin(pin, pinSalt);
  return { pinHash, pinSalt };
}
